<template>
  <div :class="['content mb-5', { 'policy-no-subtitle': !pageMeta.subTitle }]">
    <b-row class="mb-4">
      <b-col>
        <t-sub-nav
          class="mb-5"
          :loading="loading"
          :items="
            $secondaryNav.customerDefaultPolicyNavItems(
              hasVirtualLocation(selectedCustomerPolicy)
            )
          "
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <h2 class="mb-1">{{ this.$t("Notifications") }}</h2>
        <p>{{ this.$t("Get notified when a prohibited site is visited.") }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col cols="12" md="6">
        <b-form-group
          v-if="!loading"
          :label="this.$t('Send email notification to')"
          label-for="email"
          :state="$validateState($v.form.emailnotifications)"
          class="mb-0"
        >
          <b-form-input
            data-test-id="policies-notifications-email"
            :state="$validateState($v.form.emailnotifications)"
            id="email"
            v-model="form.emailnotifications"
            @blur="
              setValidationInput('emailnotifications', $event.target.value)
            "
            :placeholder="this.$t('e.g. john@acme.com')"
          />

          <template #invalid-feedback>
            <p
              v-if="
                $v.form.emailnotifications.$dirty &&
                $v.form.emailnotifications.required &&
                !$v.form.emailnotifications.email
              "
            >
              {{ $t("Must be valid email address.") }}
            </p>
            <p
              v-if="
                !$v.form.emailnotifications.required ||
                !$v.form.emailnotifications.requiredIf
              "
            >
              {{
                $t(
                  "The email address is required with the selected category notifications."
                )
              }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2>{{ this.$t("Blocked Category Notifications") }}</h2>

    <b-row v-if="!loading" class="mb-4">
      <b-col
        sm="12"
        md="6"
        lg="4"
        v-for="(item, idx) in form.categorySelection"
        :key="idx"
      >
        <t-checkbox
          white-bg
          :data-test-id="`policies-notifications-${item.name
            .toLowerCase()
            .replace(/\s/g, '-')}`"
          v-model="item.notify"
          :id="item.id.toString()"
          :value="item.notify"
          :label="item.name"
          @change="$v.form.categorySelection.$touch()"
        />
      </b-col>
    </b-row>
    <b-row v-if="loading" class="mb-4">
      <b-col sm="12" md="6" lg="4" v-for="idx in 9" :key="idx">
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <t-save-button
      data-test-id="`policies-notifications-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
  "name": "Customer Default Notifications",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import { email, requiredIf } from "vuelidate/lib/validators";
import policySubPages from "@/mixins/policySubPages";

export default {
  layout: "customer",
  mixins: [policySubPages],
  data: function () {
    return {
      fetching: false,
      loading: true,
      form: {
        emailnotifications: null,
        categorySelection: [],
      },
    };
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("categories", [
      "fetchNoticationsPolicyCategories",
      "updateCustomerNotificationsCategorySettings",
    ]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicy",
      "updateCustomerPolicy",
    ]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async onSubmit() {
      this.fetching = true;
      try {
        Promise.all([
          await this.updateCustomerPolicy({
            id: this.selectedUser.id,
            policyId: this.selectedCustomerPolicy.id,
            policy: this.form,
          }),

          await this.updateCustomerNotificationsCategorySettings({
            id: this.selectedUser.id,
            policyId: this.selectedCustomerPolicy.id,
            categorySettings: this.form.categorySelection,
          }),
        ]);
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerPolicies", ["selectedCustomerPolicy"]),
    ...mapGetters("categories", ["customerNotificationsCategories"]),
    ...mapGetters("pageMeta", ["pageMeta"]),
  },
  watch: {
    selectedCustomerPolicy() {
      this.form.emailnotifications =
        this.selectedCustomerPolicy.emailnotifications;
    },
    customerNotificationsCategories() {
      this.form.categorySelection = this.customerNotificationsCategories;
    },
  },
  validations: {
    form: {
      emailnotifications: {
        email,
        required: requiredIf(function () {
          return this.form.categorySelection.filter((c) => c.notify).length > 0;
        }),
      },
      categorySelection: {},
    },
  },
  async created() {
    await this.fetchCustomerPolicy({
      id: this.selectedUser.id,
      policyId: "default",
    });
    await this.fetchNoticationsPolicyCategories({
      id: this.selectedUser.id,
      policyId: "default",
    });

    this.loading = false;
  },
  mounted() {
    this.setPageTitle(this.$t("Default Policy"));
    this.setPageSubtitle([
      this.$t(
        "Policies determine what categories of website a user can see. This is your default policy and is automatically assigned to all new locations, devices, users and groups added to WebTitan, unless you apply a policy you have created in Content Filtering > Policies"
      ),
    ]);
  },
};
</script>
